import React, { useState } from 'react';
import './BankReconciliation.css';
import { API_BASE_URL, authFetch } from '../../config/api.config';
// T-197 (v1.8.70): Gutscheinzahlungen im Bankabgleich, Rückfragen und Hinweise
import {
  GutscheinVorschlag, gutscheinAnzeige, gutscheinRueckfrage, rechnungRueckfrage, schutzRueckfrage, zweiteRueckfrage,
  auswahlWert, parseAuswahl, zeilenHinweis, ueberzahlungDabei,
} from '../../utils/bankabgleich';

/**
 * Bankzahlungsabgleich, Erststufe (Anlage v1.4 Abschnitt 2.5 Punkt 10, Erststufe CSV):
 * CSV-Import von Kontoumsätzen (Datei-Upload oder eingefügter Text), Abgleich mit
 * offenen Rechnungen und manuelle Bestätigung der Vorschläge durch die Buchhaltung.
 * Kein Komfortkasse-API-Anschluss in dieser Stufe.
 */

interface BankTransaction {
  date?: string | null;
  amount: number;
  purpose: string;
  name: string;
  isCredit: boolean;
}

interface BankMatchSuggestion {
  invoiceId: number;
  invoiceNumber: string;
  bookingNumber: string;
  customerName: string;
  amountDue: number;
  totalAmount: number;
}

interface BankTransactionMatch {
  transaction: BankTransaction;
  confidence: string; // 'hoch' | 'mittel' | 'niedrig' | 'kein Treffer' | 'ignoriert'
  reason: string;
  suggestion?: BankMatchSuggestion | null;
  // T-197 (v1.8.70): erkannter Gutschein (confirmable = hier bestätigbar)
  voucherSuggestion?: GutscheinVorschlag | null;
}

interface BankReconciliationSummary {
  totalTransactions: number;
  creditTransactions: number;
  debitTransactions: number;
  highConfidence: number;
  mediumConfidence: number;
  lowConfidence: number;
  noMatch: number;
}

interface BankReconciliationResult {
  summary: BankReconciliationSummary;
  results: BankTransactionMatch[];
}

const BankReconciliation: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [csvText, setCsvText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [result, setResult] = useState<BankReconciliationResult | null>(null);
  const [confirmedRows, setConfirmedRows] = useState<Set<number>>(new Set());
  const [confirmingRow, setConfirmingRow] = useState<number | null>(null);
  // Stufe 2: manuelle Zuordnung bei "kein Treffer" (Liste offener Rechnungen plus Auswahl je Zeile)
  const [openInvoices, setOpenInvoices] = useState<BankMatchSuggestion[] | null>(null);
  // T-197 (v1.8.70): Gutscheine "Zahlung ausstehend" für die manuelle Zuordnung; Auswahlwert "r:<id>" bzw. "g:<id>"
  const [openVouchers, setOpenVouchers] = useState<GutscheinVorschlag[] | null>(null);
  const [manualSelection, setManualSelection] = useState<{ [rowIndex: number]: string }>({});
  // T-197 (v1.8.70): in dieser Sitzung bezahlte Rechnungen und bestätigte Gutscheine (Hinweis an weiteren Zeilen)
  const [bezahlteRechnungen, setBezahlteRechnungen] = useState<Set<number>>(new Set());
  const [bestaetigteGutscheine, setBestaetigteGutscheine] = useState<Set<number>>(new Set());
  // Review v1.8.70 Hinweis 9c: Zeilen, die als Gutschein-Zahlung bestätigt wurden (Anzeige "Bestätigt" statt "Verbucht")
  const [gutscheinZeilen, setGutscheinZeilen] = useState<Set<number>>(new Set());

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  const truncate = (text: string, maxLength: number): string => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '…' : text;
  };

  const confidenceBadgeClass = (confidence: string): string => {
    switch (confidence) {
      case 'hoch':
        return 'bankrec-badge bankrec-badge-high';
      case 'mittel':
        return 'bankrec-badge bankrec-badge-medium';
      case 'niedrig':
        return 'bankrec-badge bankrec-badge-low';
      default:
        return 'bankrec-badge bankrec-badge-none';
    }
  };

  const confidenceLabel = (row: BankTransactionMatch): string => {
    if (row.confidence === 'ignoriert') return 'Lastschrift';
    return row.confidence;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null;
    setFile(selected);
  };

  const handleReconcile = async () => {
    setError(null);
    setSuccessMessage(null);

    const hasFile = file !== null;
    const hasText = csvText.trim().length > 0;
    if (!hasFile && !hasText) {
      setError('Bitte eine CSV-Datei auswählen oder Kontoumsätze als Text einfügen.');
      return;
    }

    try {
      setLoading(true);

      let response: Response;
      if (hasFile) {
        // Datei als multipart hochladen, damit das Backend das Encoding tolerant erkennt
        const formData = new FormData();
        formData.append('file', file as File);
        // T-009: authFetch entfernt bei FormData den Content-Type-Header selbst
        // (multipart-Boundary setzt der Browser) und refresht bei 401 automatisch
        response = await authFetch(`${API_BASE_URL}/bank-reconciliation/import`, {
          method: 'POST',
          body: formData
        });
      } else {
        response = await authFetch(`${API_BASE_URL}/bank-reconciliation/import`, {
          method: 'POST',
          body: JSON.stringify({ csv: csvText })
        });
      }

      if (!response.ok) {
        let message = `HTTP ${response.status}`;
        try {
          const data = await response.json();
          if (data && data.error) message = data.error;
        } catch {
          // Antwort ohne JSON-Body, Standardmeldung verwenden
        }
        throw new Error(message);
      }

      const data: BankReconciliationResult = await response.json();
      setResult(data);
      setConfirmedRows(new Set());
      setManualSelection({});
      setBezahlteRechnungen(new Set());
      setBestaetigteGutscheine(new Set());
      setGutscheinZeilen(new Set());

      // Stufe 2: offene Rechnungen fuer die manuelle Zuordnung nachladen (best effort)
      loadOpenInvoices();
    } catch (err: any) {
      setError(`Fehler beim Abgleich: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Stufe 2: Liste offener Rechnungen fuer die manuelle Zuordnung
  const loadOpenInvoices = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/bank-reconciliation/open-invoices`, {});
      if (response.ok) {
        const data: BankMatchSuggestion[] = await response.json();
        setOpenInvoices(data);
      }
    } catch {
      // Manuelle Zuordnung ist dann nicht verfuegbar, Abgleich funktioniert trotzdem
      setOpenInvoices(null);
    }
    // T-197 (v1.8.70): Gutscheine "Zahlung ausstehend" für die manuelle Zuordnung (best effort)
    try {
      const response = await authFetch(`${API_BASE_URL}/bank-reconciliation/open-vouchers`, {});
      if (response.ok) {
        const data: GutscheinVorschlag[] = await response.json();
        setOpenVouchers(data);
      }
    } catch {
      setOpenVouchers(null);
    }
  };

  const fehlerAusAntwort = async (response: Response): Promise<{ error: string; code?: string; codes?: string[] }> => {
    try {
      const data = await response.json();
      return { error: data?.error || `HTTP ${response.status}`, code: data?.code, codes: data?.codes };
    } catch {
      return { error: `HTTP ${response.status}` };
    }
  };

  /**
   * Verbucht eine Zahlung. Stufe 2: Bei 409 (Doppel-Verbuchung oder Ueberzahlung)
   * wird die Schutzmeldung angezeigt und nach ausdruecklicher Bestaetigung mit
   * force=true wiederholt.
   */
  const submitConfirm = async (invoiceId: number, row: BankTransactionMatch, force: boolean, ueberzahlungBestaetigt = false): Promise<Response> => {
    return authFetch(`${API_BASE_URL}/bank-reconciliation/confirm`, {
      method: 'POST',
      body: JSON.stringify({
        invoiceId,
        amount: row.transaction.amount,
        paymentDate: row.transaction.date ?? null,
        reference: row.transaction.purpose,
        force,
        ueberzahlungBestaetigt
      })
    });
  };

  const handleConfirmPayment = async (rowIndex: number, row: BankTransactionMatch, manualInvoice?: BankMatchSuggestion) => {
    const target = manualInvoice ?? row.suggestion;
    if (!target) return;

    if (!window.confirm(rechnungRueckfrage(row.transaction.amount, target, !!manualInvoice))) return;

    try {
      setConfirmingRow(rowIndex);
      setError(null);

      let response = await submitConfirm(target.invoiceId, row, false);

      // Stufe 2: Schutz hat gegriffen, Rueckfrage und ggf. force-Wiederholung.
      // T-197 (v1.8.70): der Server meldet alle Hinweise zusammen (Doppel-Verbuchung mit Verwendungszwecken, Gutschein im
      // Verwendungszweck, Ueberzahlung); eine Ueberzahlung braucht nach "Trotzdem" eine zweite, ausdrueckliche Bestaetigung.
      if (response.status === 409) {
        const schutz = await fehlerAusAntwort(response);
        const zusatz = ueberzahlungDabei(schutz.code, schutz.codes)
          ? '\n\nHinweis: Wegen der Überzahlung folgt danach eine zweite Rückfrage.'
          : '';
        if (!window.confirm(schutzRueckfrage(schutz.error + zusatz))) return;
        response = await submitConfirm(target.invoiceId, row, true);
        if (response.status === 409) {
          const zweite = await fehlerAusAntwort(response);
          if (zweite.code !== 'OverpaymentConfirmRequired') throw new Error(zweite.error);
          if (!window.confirm(zweiteRueckfrage(zweite.error))) return;
          response = await submitConfirm(target.invoiceId, row, true, true);
        }
      }

      if (!response.ok) {
        const f = await fehlerAusAntwort(response);
        throw new Error(f.error);
      }

      const data = await response.json();
      const statusLabel = data.status === 'Paid' ? 'Bezahlt' : data.status === 'PartiallyPaid' ? 'Teilweise bezahlt' : data.status;
      setSuccessMessage(`Zahlung auf Rechnung ${data.invoiceNumber} verbucht, neuer Status: ${statusLabel}`);
      setConfirmedRows((previous) => {
        const next = new Set(previous);
        next.add(rowIndex);
        return next;
      });
      if (data.status === 'Paid') {
        setBezahlteRechnungen((previous) => new Set(previous).add(target.invoiceId));
      }
    } catch (err: any) {
      setError(`Fehler beim Verbuchen der Zahlung: ${err.message}`);
    } finally {
      setConfirmingRow(null);
    }
  };

  // T-197 (v1.8.70): Gutschein-Zahlung bestätigen (Gutschein frei, Gutscheinrechnung bezahlt, Mail an den Käufer)
  const handleConfirmVoucher = async (rowIndex: number, row: BankTransactionMatch, voucher: GutscheinVorschlag, manuell: boolean) => {
    if (!window.confirm(gutscheinRueckfrage(row.transaction.amount, voucher, manuell))) return;
    try {
      setConfirmingRow(rowIndex);
      setError(null);
      const response = await authFetch(`${API_BASE_URL}/bank-reconciliation/confirm-voucher`, {
        method: 'POST',
        body: JSON.stringify({
          voucherId: voucher.voucherId,
          amount: row.transaction.amount,
          paymentDate: row.transaction.date ?? null,
          reference: row.transaction.purpose
        })
      });
      if (!response.ok) {
        const f = await fehlerAusAntwort(response);
        throw new Error(f.error);
      }
      const data = await response.json();
      // Review v1.8.70 Hinweis 9b: fehlgeschlagene Gutschein-Mail nicht als Erfolg anzeigen
      if (data.emailSent === false) {
        setError(data.message || `Gutschein-Zahlung bestätigt, aber die Mail an den Käufer ging nicht hinaus (${gutscheinAnzeige(voucher)})`);
      } else {
        setSuccessMessage(data.message || `Gutschein-Zahlung bestätigt (${gutscheinAnzeige(voucher)})`);
      }
      setConfirmedRows((previous) => new Set(previous).add(rowIndex));
      setGutscheinZeilen((previous) => new Set(previous).add(rowIndex));
      setBestaetigteGutscheine((previous) => new Set(previous).add(voucher.voucherId));
      loadOpenInvoices();
    } catch (err: any) {
      setError(`Fehler beim Bestätigen der Gutschein-Zahlung: ${err.message}`);
    } finally {
      setConfirmingRow(null);
    }
  };

  return (
    <div className="bankrec-page">
      <div className="bankrec-header">
        <h1>Bankabgleich</h1>
      </div>

      <p className="bankrec-intro">
        Kontoumsätze als CSV importieren und mit offenen Rechnungen abgleichen (Erststufe, manueller CSV-Import).
        Gutschriften werden automatisch offenen Rechnungen oder Gutscheinen mit &quot;Zahlung ausstehend&quot; zugeordnet (Gutscheincode
        oder Gutscheinrechnung GS-... im Verwendungszweck), die Verbuchung erfolgt erst nach Ihrer Bestätigung.
      </p>

      {error && (
        <div className="bankrec-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Schließen</button>
        </div>
      )}
      {successMessage && (
        <div className="bankrec-success">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>Schließen</button>
        </div>
      )}

      <div className="bankrec-import-card">
        <div className="bankrec-import-row">
          <label className="bankrec-label" htmlFor="bankrec-file">CSV-Datei (z.B. Sparkassen-Export)</label>
          <input
            id="bankrec-file"
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
          />
        </div>

        <div className="bankrec-import-row">
          <label className="bankrec-label" htmlFor="bankrec-text">Oder Kontoumsätze hier einfügen</label>
          <textarea
            id="bankrec-text"
            rows={6}
            placeholder={'Buchungstag;Verwendungszweck;Beguenstigter/Zahlungspflichtiger;Betrag\n05.08.2026;RE-2026-00042 Floßfahrt;Max Mustermann;1.234,56'}
            value={csvText}
            onChange={(event) => setCsvText(event.target.value)}
          />
        </div>

        <div className="bankrec-import-actions">
          <button
            className="bankrec-btn-primary"
            onClick={handleReconcile}
            disabled={loading}
          >
            {loading ? 'Abgleich läuft ...' : 'Abgleichen'}
          </button>
        </div>
      </div>

      {result && (
        <>
          <div className="bankrec-summary">
            <span><strong>{result.summary.totalTransactions}</strong> Umsätze</span>
            <span><strong>{result.summary.creditTransactions}</strong> Gutschriften</span>
            <span><strong>{result.summary.debitTransactions}</strong> Lastschriften (ignoriert)</span>
            <span className="bankrec-summary-high"><strong>{result.summary.highConfidence}</strong> hoch</span>
            <span className="bankrec-summary-medium"><strong>{result.summary.mediumConfidence}</strong> mittel</span>
            <span className="bankrec-summary-low"><strong>{result.summary.lowConfidence}</strong> niedrig</span>
            <span className="bankrec-summary-none"><strong>{result.summary.noMatch}</strong> ohne Treffer</span>
          </div>

          <div className="bankrec-table-wrapper">
            <table className="bankrec-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th className="bankrec-col-amount">Betrag</th>
                  <th>Verwendungszweck</th>
                  <th>Name</th>
                  <th>Erkannte Rechnung / Gutschein</th>
                  <th>Konfidenz</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((row, index) => {
                  const isConfirmed = confirmedRows.has(index);
                  const gutschein = row.voucherSuggestion ?? null;
                  const hinweis = isConfirmed ? null : zeilenHinweis(row.suggestion?.invoiceId, gutschein?.voucherId, bezahlteRechnungen, bestaetigteGutscheine);
                  const manuellMoeglich = row.transaction.isCredit &&
                    ((openInvoices && openInvoices.length > 0) || (openVouchers && openVouchers.length > 0));
                  return (
                    <tr key={index} className={isConfirmed ? 'bankrec-row-done' : row.transaction.isCredit ? '' : 'bankrec-row-debit'}>
                      <td>{formatDate(row.transaction.date)}</td>
                      <td className={`bankrec-col-amount ${row.transaction.isCredit ? 'bankrec-amount-credit' : 'bankrec-amount-debit'}`}>
                        {formatCurrency(row.transaction.amount)}
                      </td>
                      <td title={row.transaction.purpose}>{truncate(row.transaction.purpose, 50)}</td>
                      <td title={row.transaction.name}>{truncate(row.transaction.name, 30)}</td>
                      <td>
                        {gutschein ? (
                          /* T-197 (v1.8.70): erkannter Gutschein */
                          <div className="bankrec-suggestion" title={row.reason}>
                            <div className="bankrec-suggestion-number">Gutschein {gutschein.invoiceNumber || gutschein.code}</div>
                            <div className="bankrec-suggestion-details">
                              {gutschein.buyerName || 'Käufer unbekannt'}, Wert: {formatCurrency(gutschein.amount)}, {gutschein.statusText || gutschein.status}
                            </div>
                          </div>
                        ) : row.suggestion ? (
                          <div className="bankrec-suggestion" title={row.reason}>
                            <div className="bankrec-suggestion-number">{row.suggestion.invoiceNumber}</div>
                            <div className="bankrec-suggestion-details">
                              {row.suggestion.customerName || 'Kunde unbekannt'}, offen: {formatCurrency(row.suggestion.amountDue)}
                            </div>
                          </div>
                        ) : (
                          <span className="bankrec-no-suggestion" title={row.reason}>-</span>
                        )}
                        {/* T-197 (v1.8.70): Begründung sichtbar, wenn ein Gutschein genannt, aber nicht bestätigbar ist */}
                        {!isConfirmed && gutschein && !gutschein.confirmable && (
                          <div className="bankrec-suggestion-details" style={{ color: '#b45309', marginTop: 4 }}>{row.reason}</div>
                        )}
                        {!isConfirmed && !gutschein && !row.suggestion && row.transaction.isCredit && /GS[\s\-_.\/]*\d{4}/i.test(row.transaction.purpose || '') && (
                          <div className="bankrec-suggestion-details" style={{ color: '#b45309', marginTop: 4 }}>{row.reason}</div>
                        )}
                      </td>
                      <td>
                        <span className={confidenceBadgeClass(row.confidence)} title={row.reason}>
                          {confidenceLabel(row)}
                        </span>
                      </td>
                      <td>
                        {hinweis && <div className="bankrec-suggestion-details" style={{ color: '#b91c1c', marginBottom: 4 }}>{hinweis}</div>}
                        {isConfirmed ? (
                          <span className="bankrec-done">{gutscheinZeilen.has(index) ? 'Bestätigt' : 'Verbucht'}</span>
                        ) : gutschein && gutschein.confirmable ? (
                          <button
                            className="bankrec-btn-secondary"
                            onClick={() => handleConfirmVoucher(index, row, gutschein, false)}
                            disabled={confirmingRow !== null}
                            title="Gutschein freischalten, Gutscheinrechnung auf bezahlt, Mail mit Gutschein-PDF an den Käufer"
                          >
                            {confirmingRow === index ? 'Wird bestätigt ...' : 'Gutschein-Zahlung bestätigen'}
                          </button>
                        ) : row.suggestion ? (
                          <button
                            className="bankrec-btn-secondary"
                            onClick={() => handleConfirmPayment(index, row)}
                            disabled={confirmingRow !== null}
                          >
                            {confirmingRow === index ? 'Wird verbucht ...' : 'Zahlung verbuchen'}
                          </button>
                        ) : manuellMoeglich ? (
                          /* Stufe 2: manuelle Zuordnung bei "kein Treffer"; T-197 (v1.8.70): auch Gutscheine "Zahlung ausstehend" */
                          <div className="bankrec-manual">
                            <select
                              className="bankrec-manual-select"
                              value={manualSelection[index] ?? ''}
                              onChange={(event) => {
                                const value = event.target.value;
                                setManualSelection((previous) => ({ ...previous, [index]: value }));
                              }}
                            >
                              <option value="">Rechnung oder Gutschein wählen ...</option>
                              {openInvoices && openInvoices.length > 0 && (
                                <optgroup label="Offene Rechnungen">
                                  {openInvoices.map((inv) => (
                                    <option key={`r${inv.invoiceId}`} value={auswahlWert('r', inv.invoiceId)}>
                                      {inv.invoiceNumber} | {truncate(inv.customerName || 'Kunde unbekannt', 22)} | offen {formatCurrency(inv.amountDue)}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                              {openVouchers && openVouchers.length > 0 && (
                                <optgroup label="Gutscheine (Zahlung ausstehend)">
                                  {openVouchers.map((g) => (
                                    <option key={`g${g.voucherId}`} value={auswahlWert('g', g.voucherId)}>
                                      Gutschein {g.invoiceNumber || g.code} | {truncate(g.buyerName || 'Käufer unbekannt', 22)} | {formatCurrency(g.amount)}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                            <button
                              className="bankrec-btn-secondary"
                              onClick={() => {
                                const auswahl = parseAuswahl(manualSelection[index]);
                                if (!auswahl) return;
                                if (auswahl.art === 'g') {
                                  const g = (openVouchers || []).find((v) => v.voucherId === auswahl.id);
                                  if (g) handleConfirmVoucher(index, row, g, true);
                                } else {
                                  const invoice = (openInvoices || []).find((inv) => inv.invoiceId === auswahl.id);
                                  if (invoice) handleConfirmPayment(index, row, invoice);
                                }
                              }}
                              disabled={confirmingRow !== null || !parseAuswahl(manualSelection[index])}
                            >
                              {confirmingRow === index ? 'Wird verbucht ...' : 'Zuordnen'}
                            </button>
                          </div>
                        ) : (
                          <span className="bankrec-no-action">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BankReconciliation;
